/**
 * The register list's page head.
 *
 * Its own file because the head used to be the busiest thing on the screen and
 * had to stop being that. It carried a title, a statistics button wedged into
 * the heading, a two-line sentence, two text links dressed as prose, and two
 * data cards holding seven controls and five numbers between them - all above
 * the search box, all before a single company had been read.
 *
 * What is left says three things and no more: what this is, how much of it there
 * is, and whether the data behind it is current. The two links became buttons in
 * the toolbar where the rest of the controls live, and everything pressable that
 * rewrites data went into one menu.
 */
import { BarChart3 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Btn, PageHead, fmtNum } from "../../ui";
import { AriregisterStats, type ProspectPreset } from "./AriregisterStats";
import { DataMenu, DataStatus, type SyncState, type WebNeedPassState } from "./data";

export function ListHead({
  canManage,
  sync,
  pass,
  onApplyProspect,
}: {
  canManage: boolean;
  sync: SyncState;
  pass: WebNeedPassState;
  onApplyProspect: (p: ProspectPreset) => void;
}) {
  const t = useT();
  const active = sync.status?.stats.activeCount ?? null;

  return (
    <PageHead
      title={
        <span className="flex flex-wrap items-baseline gap-3">
          {t("nav.ariregister")}
          {/* The size of the thing you are about to search, next to its name.
              It used to be the first line of a status card three controls down
              the page, which is a strange place to keep the one number that
              tells you whether the mirror has loaded at all. */}
          {active !== null ? (
            <span className="crm-count crm-tnum">{t("ariregister.headCount", { count: fmtNum(active) })}</span>
          ) : null}
        </span>
      }
      sub={t("ariregister.intro")}
      actions={
        <>
          <DataStatus sync={sync} pass={pass} />
          <AriregisterStats onApplyProspect={onApplyProspect}>
            <Btn>
              <BarChart3 className="size-4" />
              {t("ariregister.stats.open")}
            </Btn>
          </AriregisterStats>
          {/* Gated on the permission, not disabled by it: an operator who may
              not refresh the mirror has no use for a button that says so. */}
          {canManage ? <DataMenu sync={sync} pass={pass} /> : null}
        </>
      }
    />
  );
}
